import { flowRight, get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from '../../../common/components/runtime-context';
import classNames from 'classnames';
import PostImage, { PostGif } from '../post-image';
import PostHeader from '../post-header';
import PostTitle from '../post-title';
import PostSubTitle from '../post-sub-title';
import PostHashtags from '../post-hashtags';
import PostFooter from '../post-footer';
import ContentExcerpt from '../content-excerpt';
import PostVideoEmbed from '../post-video-embed';
import {
  getPostImageCover,
  getPostVideoEmbedCover,
  getPostGiphyCover,
} from '../../../common/services/get-post-cover';
import getOuterUrl from '../../services/get-outer-url';
import { getCategory } from '../../selectors/categories-selectors';
import { getIsMultiplePostTypeCategory } from '../../services/categories-service';
import contentStateToString from '../../services/content-state-to-string';
import {
  getIsListHashtagsEnabled,
  getIsViewsCountEnabled,
  getIsSocialSharingLinksEnabled,
} from '../../selectors/app-settings-selectors';
import { isQACategoriesExperimentEnabled } from '../../selectors/experiments-selectors';
import Link from '../link/internal-link';
import { QUESTION } from '../../constants/post-types';
import withCardBorderWidth from '../../hoc/with-card-border-width';
import withFontClassName from '../../hoc/with-font-class-name';
import withPermissions from '../../hoc/with-permissions';
import withAuth from '../../hoc/with-auth';
import styles from './post-list-item.scss';

const renderImage = (image, url, isPublic, type) => {
  return image && <PostImage image={image} url={url} isPublic={isPublic} type={type} />;
};

const renderGiphy = (gif, type) => {
  return gif && <PostGif gif={gif} type={type} />;
};

const renderVideoEmbed = oembed => {
  return oembed && <PostVideoEmbed oembed={oembed} />;
};

export const PostListItem = ({
  type,
  category,
  post,
  query,
  borderWidth,
  titleFontClassName,
  contentFontClassName,
  can,
  forPublicUser,
  onLikeClick,
  isViewsCountEnabled,
  isListHashtagsEnabled,
  showCategoryLink,
  showContent,
  isSocialSharingLinksEnabled,
  showPinIcon,
  showLockIcon,
  showTopComment,
  showMoreButton,
  showViewCount,
  isQACategoriesEnabled,
  origin,
  showSeparator,

}) => {
  const isSearch = type === PostListItem.SEARCH;
  const image = !isSearch && getPostImageCover(post);
  const videoEmbed = !isSearch && getPostVideoEmbedCover(post);
  const giphyEmbed = !isSearch && getPostGiphyCover(post);
  const className = classNames(
    styles.container,
    styles[type],
    contentFontClassName,
    'forum-text-color',
    'forum-card-background-color',
    'forum-card-border-color',
    'post-list-item',
    { [styles.withImage]: image || videoEmbed },
  );

  const postLink = `/${category.slug}/${post.slug}`;
  const hashtags = get(post, 'hashtags');

  let coverMedia;

  if (videoEmbed) {
    coverMedia = renderVideoEmbed(videoEmbed);
  } else if (image) {
    coverMedia = renderImage(
      image,
      getOuterUrl(postLink),
      isSocialSharingLinksEnabled && can('share', 'post', post),
      type,
    );
  } else if (giphyEmbed) {
    coverMedia = renderGiphy(giphyEmbed, type);
  }
  const content = contentStateToString(post.content);
  const isEmptyContent = content.trim() === '';
  return (
    <div className={className} style={{ borderWidth }} role="article" data-hook="post-list-item">
      <PostHeader
        type={isQACategoriesEnabled ? PostHeader.SMALL : type}
        post={post}
        showLastActivityDate={type !== 'membersArea'}
        showDateBelowAvatar={false}
        showAvatarBadges
        showStrippedBadges
        showPinIcon={showPinIcon}
        showLockIcon={showLockIcon}
        showTopComment={
          showTopComment && (!isQACategoriesEnabled || (post && post.postType === QUESTION))
        }
        showMoreButton={showMoreButton}
        origin={origin}
        isQACategoriesEnabled={isQACategoriesEnabled}
      />
      <PostTitle
        className={classNames(titleFontClassName, 'forum-text-color')}
        type={type}
        title={post.title}
        query={query}
        to={postLink}
        post={post}
        isQACategoriesEnabled={isQACategoriesEnabled}
        showTopComment={false}
      />
      <PostSubTitle
        post={post}
        type={type}
        category={category}
        showPostType={getIsMultiplePostTypeCategory(category)}
        isQACategoriesEnabled={isQACategoriesEnabled}
      />
      {showContent && !isEmptyContent && (
        <div className={classNames(styles.content)}>
          <ContentExcerpt
            type={type}
            content={content.slice(0, 200)}
            query={query}
            isContentConverted
            isQACategoriesEnabled={isQACategoriesEnabled}
            showCategoryLink={showCategoryLink}
          />
        </div>
      )}
      {isListHashtagsEnabled && !isEmpty(hashtags) && (
        <PostHashtags type={type} hashtags={hashtags} />
      )}
      {showContent && coverMedia && <Link to={postLink}>{coverMedia}</Link>}
      <div className={classNames({ [styles.extraFooterSpacing]: type === PostListItem.LARGE })}>
        <PostFooter
          type={type}
          forPublicUser={forPublicUser}
          onLikeClick={onLikeClick}
          post={post}
          Link={Link}
          postLink={postLink}
          isViewsCountEnabled={isViewsCountEnabled && showViewCount}
          isListHashtagsEnabled={isListHashtagsEnabled}
          showSeparator={isQACategoriesEnabled || showSeparator}
          isQACategoriesEnabled={isQACategoriesEnabled}
        />
      </div>
    </div>
  );
};

PostListItem.SMALL = 'small';
PostListItem.MEDIUM = 'medium';
PostListItem.LARGE = 'large';
PostListItem.SEARCH = 'search';
PostListItem.MEMBERS_AREA = 'membersArea';
PostListItem.MOBILE = 'mobile';

PostListItem.propTypes = {
  onLikeClick: PropTypes.func.isRequired,
  t: PropTypes.func,
  category: PropTypes.object,
  post: PropTypes.object.isRequired,
  query: PropTypes.string,
  type: PropTypes.string,
  borderWidth: PropTypes.number,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  can: PropTypes.func,
  navigation: PropTypes.object,
  forPublicUser: PropTypes.func.isRequired,
  isViewsCountEnabled: PropTypes.bool.isRequired,
  isListHashtagsEnabled: PropTypes.bool.isRequired,
  showCategoryLink: PropTypes.bool,
  showContent: PropTypes.bool,
  isSocialSharingLinksEnabled: PropTypes.bool,
  showPinIcon: PropTypes.bool,
  showLockIcon: PropTypes.bool,
  showTopComment: PropTypes.bool,
  showMoreButton: PropTypes.bool,
  showViewCount: PropTypes.bool,
  origin: PropTypes.string.isRequired,
  showSeparator: PropTypes.bool,
};

PostListItem.defaultProps = {
  showContent: true,
  showPinIcon: true,
  showLockIcon: true,
  showTopComment: true,
  showMoreButton: true,
  showViewCount: true,
};

const mapRuntimeToProps = (state, ownProps, actions, host) => ({
  category: getCategory(state, ownProps.post.categoryId),
  isViewsCountEnabled: getIsViewsCountEnabled(state, host.style),
  isListHashtagsEnabled: getIsListHashtagsEnabled(state, host.style),
  isSocialSharingLinksEnabled: getIsSocialSharingLinksEnabled(state, host.style),
  isQACategoriesEnabled: isQACategoriesExperimentEnabled(state),
});

export default flowRight(
  connect(mapRuntimeToProps),
  withCardBorderWidth,
  withFontClassName,
  withPermissions,
  withAuth,
)(PostListItem);
